import { Button, Typography } from '@themenu/design-system'
import { type FC, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { logEvent } from '../../../services/datadog'
import { ActivationStepLayout } from './ActivationStepLayout'
import { Confetti } from './Confetti'

/**
 * Terminal success screen. Finalize has issued the directory JWT; the
 * post-activation destination is FE-owned (contract §12) and resolved by the
 * platform-aware `onContinue` handler in `ActivationFlow` (web → employee app,
 * native → OAuth callback that closes the iOS/Android auth webview). The user exits
 * the flow manually via the continue button; there is no automatic redirect.
 *
 * Presentational: the continue handler is now async (native replays the OAuth
 * exchange before redirecting), so the button is disabled while in flight to avoid a
 * double-tap firing two `/session` calls.
 */
export type SuccessStepProps = {
  onContinue: () => void | Promise<void>
}

export const SuccessStep: FC<SuccessStepProps> = ({ onContinue }) => {
  const { formatMessage } = useIntl()
  const [continuing, setContinuing] = useState(false)
  // Synchronous re-entry guard: `continuing` state only updates on the next render,
  // so two taps fired within the same tick would both pass a state-based check and
  // fire `onContinue` twice. The ref blocks the second tap immediately; the state
  // drives the visual `disabled`.
  const inFlight = useRef(false)

  const handleContinue = async (): Promise<void> => {
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    setContinuing(true)
    try {
      await onContinue()
    } catch (error) {
      // The redirect failed (e.g. the native OAuth replay): log it so a regression of
      // the webview-close path stays observable (the rest of the funnel logs via
      // `logEvent`), then re-enable the button so the user can retry instead of being
      // dead-ended on the success screen.
      logEvent('br_activation_continue_error', undefined, {}, error as Error, 'warn')
      inFlight.current = false
      setContinuing(false)
    }
  }

  return (
    <>
    <Confetti />
    <ActivationStepLayout
      titleId="br_activation.success.title"
      testId="br-activation-success"
      centered
      footer={
        <Button
          data-testid="br-activation-success-continue"
          variant="primary"
          className="w-full"
          disabled={continuing}
          onClick={handleContinue}
        >
          {formatMessage({ id: 'br_activation.success.continue' })}
        </Button>
      }
    >
      <Typography variant="body" component="p">
        {formatMessage({ id: 'br_activation.success.body' })}
      </Typography>
    </ActivationStepLayout>
    </>
  )
}
